import { filterPatientAppointments } from '../utils/patientAppointmentHistory';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Edit3,
  Trash2,
  Activity,
  Save,
  Sparkles,
} from 'lucide-react';
import axios from 'axios';
import AppointmentFormModal from './appointments/AppointmentFormModal';
import AppointmentFinancialCompletionModal from './appointments/AppointmentFinancialCompletionModal';
import { useAuth } from '../contexts/AuthContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const STATUS_COLORS = {
  Agendado: '#3B82F6',
  'Não Compareceu': '#F59E0B',
  Realizado: '#10B981',
  Cancelado: '#EF4444',
};

const statusColor = (status) => STATUS_COLORS[status] || '#6B7280';

const CHECK_IN_ALLOWED_STATUSES = new Set(['Agendado', 'Confirmado']);
const canStartAppointment = (appointment) =>
  CHECK_IN_ALLOWED_STATUSES.has(appointment?.status);

const ALIGNER_SITUATIONS = {
  awaiting_documents: 'Aguardando documentação/exames',
  awaiting_aligners: 'Aguardando alinhadores',
  follow_up_only: 'Em acompanhamento',
  awaiting_refinement: 'Aguardando refinamento / novos alinhadores',
  refinement_active: 'Em refinamento',
  awaiting_retention: 'Aguardando contenção',
  completed: 'Finalizado',
};

const getSimpleAlignerSituation = (item) => {
  if (item?.status === 'completed') return 'completed';
  if (item?.phase_status === 'final_follow_up') return 'follow_up_only';
  return Object.prototype.hasOwnProperty.call(
    ALIGNER_SITUATIONS,
    item?.phase_status
  )
    ? item.phase_status
    : 'follow_up_only';
};

const hasAllAlignersDelivered = (item) =>
  item?.all_aligners_delivered === true ||
  item?.phase_status === 'final_follow_up';

const orderKey = (a) =>
  `${a.appointment_date || ''} ${a.start_time || '00:00'}`;

const addMonthsToDate = (baseDate, months) => {
  const base =
    baseDate && /^\d{4}-\d{2}-\d{2}$/.test(baseDate)
      ? new Date(`${baseDate}T00:00:00`)
      : new Date();

  base.setMonth(base.getMonth() + months);

  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, '0');
  const d = String(base.getDate()).padStart(2, '0');

  return `${y}-${m}-${d}`;
};

export default function AppointmentsTab({
  patientData,
  selectedPatient,
  appointmentsLoading = false,
  appointmentLoadError = '', 
  treatments = [],
  toast,
  showToast,
  onDataUpdate,
  onAppointmentCompleted,
}) {
  const { user } = useAuth();
  const permissions = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);
  const can = (permission) => permissions.has(permission);
  const [localAppointments, setLocalAppointments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [initialAppointmentData, setInitialAppointmentData] = useState(null);
  const [clinicSettings, setClinicSettings] = useState(null);

  // agendamento em atendimento (lido do localStorage)
  const [activeAppt, setActiveAppt] = useState(null);
  const [activeEvolutionText, setActiveEvolutionText] = useState('');
  const [activeTreatmentId, setActiveTreatmentId] = useState('');
  const [alignerTreatments, setAlignerTreatments] = useState([]);
  const [activeAlignerTreatmentId, setActiveAlignerTreatmentId] = useState('');
  const [updateAlignerTreatment, setUpdateAlignerTreatment] = useState(false);
  const [alignerSituationAfter, setAlignerSituationAfter] = useState('follow_up_only');
  const [alignerNextSetReceived, setAlignerNextSetReceived] = useState(false);
  const [alignerNextSetReceivedAt, setAlignerNextSetReceivedAt] = useState('');
  const [alignerNextSetStart, setAlignerNextSetStart] = useState('');
  const [alignerNextSetEnd, setAlignerNextSetEnd] = useState('');
  const [alignerTotalStages, setAlignerTotalStages] = useState('');
  const [alignerAllDelivered, setAlignerAllDelivered] = useState(false);
  const [savingEvolution, setSavingEvolution] = useState(false);
  const [showFinancialCompletion, setShowFinancialCompletion] = useState(false);
  const [createReturnReminder, setCreateReturnReminder] = useState(false);
  const [returnReminderDate, setReturnReminderDate] = useState('');
  const [returnReminderTime, setReturnReminderTime] = useState('');
  const [returnReminderTitle, setReturnReminderTitle] = useState('');

  const patientId = selectedPatient?.id || patientData?.id || '';

  // Lê o activeAppointment do localStorage quando o componente monta
  // Lê o activeAppointment do localStorage quando o componente monta
  useEffect(() => {
    try {
      const stored = localStorage.getItem('activeAppointment');
      if (stored) {
        const obj = JSON.parse(stored);
        if (obj?.patient_id === patientId) {
          setActiveAppt(obj);
          setActiveEvolutionText(obj.evolution_notes || '');
          setActiveTreatmentId(obj.treatment_id || '');
        } else {
          setActiveAppt(null);
          setActiveEvolutionText('');
          setActiveTreatmentId('');
        }
      } else {
        setActiveAppt(null);
        setActiveEvolutionText('');
        setActiveTreatmentId('');
      }

      // sempre limpa os campos de lembrete ao trocar paciente/atendimento
      setCreateReturnReminder(false);
      setReturnReminderDate('');
      setReturnReminderTime('');
      setReturnReminderTitle('');
    } catch {
      setActiveAppt(null);
      setActiveEvolutionText('');
      setActiveTreatmentId('');
      setCreateReturnReminder(false);
      setReturnReminderDate('');
      setReturnReminderTime('');
      setReturnReminderTitle('');
    }
  }, [patientId]);

  useEffect(() => {
    let mounted = true;

    const loadAlignerTreatments = async () => {
      if (!patientId) {
        setAlignerTreatments([]);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${API_URL}/aligner-treatments/dashboard`,
          {
            params: { patient_id: patientId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!mounted) return;

        const openItems = (data?.items || []).filter(
          (item) => !['completed', 'cancelled'].includes(item?.status)
        );
        setAlignerTreatments(openItems);
      } catch (error) {
        if (mounted) setAlignerTreatments([]);
        if (![403, 404].includes(error?.response?.status)) {
          console.error('Erro ao verificar tratamentos com alinhadores:', error);
        }
      }
    };

    loadAlignerTreatments();
    return () => {
      mounted = false;
    };
  }, [patientId]);

  useEffect(() => {
    if (!activeAppt || alignerTreatments.length === 0) {
      setActiveAlignerTreatmentId('');
      setUpdateAlignerTreatment(false);
      setAlignerNextSetReceived(false);
      setAlignerNextSetReceivedAt('');
      setAlignerNextSetStart('');
      setAlignerNextSetEnd('');
      setAlignerTotalStages('');
      setAlignerAllDelivered(false);
      return;
    }

    const explicitId = activeAppt.aligner_treatment_id || '';
    const linkedByClinicalTreatment = alignerTreatments.find(
      (item) => item.linked_treatment_id && item.linked_treatment_id === activeAppt.treatment_id
    );
    const selected =
      alignerTreatments.find((item) => item.id === explicitId) ||
      linkedByClinicalTreatment ||
      (alignerTreatments.length === 1 ? alignerTreatments[0] : null);

    if (selected) {
      setActiveAlignerTreatmentId(selected.id);
      setAlignerSituationAfter(getSimpleAlignerSituation(selected));
      setUpdateAlignerTreatment(true);
      setAlignerNextSetReceived(selected.next_set_received === true);
      setAlignerNextSetReceivedAt(selected.next_set_received_at || '');
      setAlignerNextSetStart(selected.next_set_start_stage ?? '');
      setAlignerNextSetEnd(selected.next_set_end_stage ?? '');
      setAlignerTotalStages(selected.total_stages || '');
      setAlignerAllDelivered(hasAllAlignersDelivered(selected));
    } else {
      setActiveAlignerTreatmentId('');
      setUpdateAlignerTreatment(false);
      setAlignerNextSetReceived(false);
      setAlignerNextSetReceivedAt('');
      setAlignerNextSetStart('');
      setAlignerNextSetEnd('');
      setAlignerTotalStages('');
      setAlignerAllDelivered(false);
    }
  }, [activeAppt, alignerTreatments]);

  // sincroniza lista local com o que vem do pai
  useEffect(() => {
    const parentAll = Array.isArray(patientData?.appointments)
      ? patientData.appointments
      : [];
    setLocalAppointments(filterPatientAppointments(parentAll, patientId));
  }, [patientData?.appointments, patientId]);

  // quando algum lugar dispara "appointments:changed", recarrega pelo pai
  useEffect(() => {
    const handler = () => onDataUpdate && onDataUpdate();
    window.addEventListener('appointments:changed', handler);
    return () => window.removeEventListener('appointments:changed', handler);
  }, [onDataUpdate]);

  useEffect(() => {
    let mounted = true;

    async function loadClinicSettings() {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/clinic-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (mounted) {
          setClinicSettings(data || null);
        }
      } catch (e) {
        console.error('Erro ao carregar configurações da clínica:', e);
      }
    }

    loadClinicSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const sorted = useMemo(() => {
    const list = [...localAppointments];
    list.sort((a, b) => orderKey(b).localeCompare(orderKey(a)));
    return list;
  }, [localAppointments]);

  if (!patientId) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-100 p-3 rounded">
        Paciente não informado.
      </div>
    );
  }

  const getActiveProfessionals = () => {
    const list = clinicSettings?.professionals || [];
    const active = list.filter((p) => p?.id && p?.name && p?.is_active !== false);

    if (active.length > 0) return active;

    if (clinicSettings?.professional_name) {
      return [
        {
          id: 'main',
          name: clinicSettings.professional_name,
          is_main: true,
        },
      ];
    }

    return [];
  };

  const getSingleActiveProfessional = () => {
    const activeProfessionals = getActiveProfessionals();
    return activeProfessionals.length === 1 ? activeProfessionals[0] : null;
  };

  const currentPatientForModal = selectedPatient?.id
    ? {
        id: selectedPatient.id,
        full_name: selectedPatient.full_name || selectedPatient.name || '',
        phone: selectedPatient.phone || selectedPatient.mobile_phone || '',
      }
    : patientData?.id
    ? {
        id: patientData.id,
        full_name: patientData.full_name || patientData.name || '',
        phone: patientData.phone || patientData.mobile_phone || '',
      }
    : null;
  
  const openCreate = () => {
    const singleProfessional = getSingleActiveProfessional?.();

    setEditing(null);

    setInitialAppointmentData({
      patient_id: currentPatientForModal?.id || patientId,
      patient_name: currentPatientForModal?.full_name || '',
      patient_phone: currentPatientForModal?.phone || '',
      ...(singleProfessional
        ? {
            professional_id: singleProfessional.id,
            professional_name: singleProfessional.name,
          }
        : {}),
    });

    setShowForm(true);
  };

  const openEdit = (apt) => {
    setInitialAppointmentData(null);
    setEditing(apt);
    setShowForm(true);
  };

  const handleFormClose = (changed) => {
    setShowForm(false);
    setEditing(null);
    setInitialAppointmentData(null);
    if (changed) {
      onDataUpdate && onDataUpdate();
      window.dispatchEvent(new CustomEvent('appointments:changed'));
    }
  };

  const handleDelete = async (apt) => {
    if (!window.confirm('Tem certeza que deseja excluir este agendamento?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/appointments/${apt.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLocalAppointments((prev) => prev.filter((x) => x.id !== apt.id));
      window.dispatchEvent(new CustomEvent('appointments:changed'));
      onDataUpdate && onDataUpdate();
      showToast?.('Agendamento excluído!', 'success');
    } catch (e) {
      console.error('Erro ao excluir agendamento:', e);
      showToast?.(
        e?.response?.status === 403
          ? 'Você não tem permissão para excluir agendamentos. Altere o status para Cancelado.'
          : (e?.response?.data?.detail || 'Erro ao excluir agendamento'),
        'error'
      );
    }
  };

  // inicia atendimento (check-in)
  const handleCheckIn = async (apt) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const { data: all } = await axios.get(`${API_URL}/appointments`, {
        headers,
      });
      const list = Array.isArray(all) ? all : all?.items || [];
      const found = list.find((a) => a.id === apt.id);
      if (!found) {
        toast?.error?.('Agendamento não encontrado.');
        return;
      }

      if (!canStartAppointment(found)) {
        localStorage.removeItem('activeAppointment');
        setActiveAppt(null);
        toast?.error?.('Este agendamento já foi encerrado ou não permite iniciar atendimento.');
        return;
      }

      const active = {
        ...found,
        patient_id: patientId,
      };

      localStorage.setItem('activeAppointment', JSON.stringify(active));
      setActiveAppt(active);
      setActiveEvolutionText(active.evolution_notes || '');
      setActiveTreatmentId(active.treatment_id || '');

      window.dispatchEvent(new CustomEvent('appointments:changed'));
      onDataUpdate && onDataUpdate();
      toast?.success?.('Atendimento iniciado.');
    } catch (e) {
      console.error('Erro no check-in:', e);
      toast?.error?.(
        e?.response?.data?.detail || 'Erro ao iniciar atendimento.'
      );
    }
  };

  const handleCancelActiveAppointment = () => {
    localStorage.removeItem('activeAppointment');
    setActiveAppt(null);
    setActiveEvolutionText('');
    setActiveTreatmentId('');
    setActiveAlignerTreatmentId('');
    setUpdateAlignerTreatment(false);
    setAlignerSituationAfter('follow_up_only');
    setCreateReturnReminder(false);
    setReturnReminderDate('');
    setReturnReminderTime('');
    setReturnReminderTitle('');
  };

  // conclui atendimento (check-out)
  const handleFinish = async () => {
    if (!activeAppt?.id) return;

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const { data: all } = await axios.get(`${API_URL}/appointments`, {
        headers,
      });
      const list = Array.isArray(all) ? all : all?.items || [];
      const found = list.find((a) => a.id === activeAppt.id);
      if (!found) {
        toast?.error?.('Agendamento não encontrado para concluir.');
        return;
      }

      const updateData = {
        ...found,
        evolution_notes: activeEvolutionText.trim(),
        treatment_id: activeTreatmentId || null,
        status: 'Realizado',
      };

      await axios.put(`${API_URL}/appointments/${activeAppt.id}`, updateData, {
        headers: { ...headers, 'Content-Type': 'application/json' },
      });

      localStorage.removeItem('activeAppointment');
      setActiveAppt(null);
      setActiveEvolutionText('');
      setActiveTreatmentId('');
      setCreateReturnReminder(false);
      setReturnReminderDate('');
      setReturnReminderTime('');
      setReturnReminderTitle('');

      window.dispatchEvent(new CustomEvent('appointments:changed'));
      onDataUpdate && onDataUpdate();
      toast?.success?.('Atendimento concluído.');
      onAppointmentCompleted && onAppointmentCompleted();
    } catch (e) {
      console.error('Erro ao concluir atendimento:', e);
      toast?.error?.(
        e?.response?.data?.detail || 'Erro ao concluir atendimento.'
      );
    }
  };

  // Salva a evolução do atendimento em andamento (sem concluir ainda)
  const handleSaveEvolution = async () => {
    if (!activeAppt?.id) return;
    if (updateAlignerTreatment && !activeAlignerTreatmentId) {
      toast?.error?.('Selecione qual tratamento de alinhadores deve ser atualizado.');
      return;
    }
    setSavingEvolution(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const { data: all } = await axios.get(`${API_URL}/appointments`, {
        headers,
      });
      const list = Array.isArray(all) ? all : all?.items || [];
      const found = list.find((a) => a.id === activeAppt.id);
      if (!found) {
        toast?.error?.('Agendamento não encontrado para atualização.');
        return;
      }

      const newEvolutionNotes = activeEvolutionText.trim();
      const newTreatmentId = activeTreatmentId || null;

      const updateData = {
        ...found,
        evolution_notes: newEvolutionNotes,
        treatment_id: newTreatmentId,
        aligner_treatment_id:
          updateAlignerTreatment && activeAlignerTreatmentId
            ? activeAlignerTreatmentId
            : found.aligner_treatment_id || null,
        status: found.status,
      };

      await axios.put(`${API_URL}/appointments/${activeAppt.id}`, updateData, {
        headers: { ...headers, 'Content-Type': 'application/json' },
      });

      if (updateAlignerTreatment && activeAlignerTreatmentId) {
        const selectedAligner = alignerTreatments.find(
          (item) => item.id === activeAlignerTreatmentId
        );

        try {
          await axios.post(
            `${API_URL}/aligner-treatments/${activeAlignerTreatmentId}/events`,
            {
              event_type: 'in_person_visit',
              event_date: found.appointment_date,
              title: found.procedure || 'Consulta presencial',
              description: newEvolutionNotes || null,
              professional_id: found.professional_id || selectedAligner?.professional_id || null,
              professional_name: found.professional_name || selectedAligner?.professional_name || null,
              register_in_clinical_evolution: false,
              phase_status_after: alignerSituationAfter,
              all_aligners_delivered: alignerAllDelivered,
              next_follow_up_date:
                createReturnReminder && returnReminderDate
                  ? returnReminderDate
                  : selectedAligner?.next_follow_up_date || null,
              next_set_received:
                !['completed', 'awaiting_retention'].includes(alignerSituationAfter) &&
                !alignerAllDelivered &&
                alignerNextSetReceived,
              next_set_received_at:
                !['completed', 'awaiting_retention'].includes(alignerSituationAfter) &&
                !alignerAllDelivered &&
                alignerNextSetReceived
                  ? alignerNextSetReceivedAt || found.appointment_date
                  : null,
              next_set_start_stage: alignerNextSetReceived && !alignerAllDelivered
                ? Number(alignerNextSetStart)
                : null,
              next_set_end_stage: alignerNextSetReceived && !alignerAllDelivered
                ? Number(alignerNextSetEnd)
                : null,
              total_stages: alignerTotalStages
                ? Number(alignerTotalStages)
                : null,
              appointment_id: activeAppt.id,
            },
            { headers: { ...headers, 'Content-Type': 'application/json' } }
          );

        } catch (alignerError) {
          console.error('Erro ao atualizar o acompanhamento do alinhador:', alignerError);
          toast?.error?.(
            alignerError?.response?.data?.detail ||
              'A evolução foi salva, mas não foi possível atualizar o acompanhamento do alinhador. Tente salvar novamente.'
          );
          return;
        }
      }

      setLocalAppointments((prev) =>
        (prev || []).map((a) =>
          a.id === activeAppt.id ? { ...a, ...updateData } : a
        )
      );

      // Cria lembrete de retorno, se solicitado
      if (createReturnReminder && returnReminderDate) {
        try {
          const patientName =
            activeAppt.patient_name ||
            selectedPatient?.full_name ||
            patientData?.full_name ||
            '';

          const titleBase =
            returnReminderTitle.trim() ||
            `Retorno - ${patientName || 'Paciente'}`;

          const payload = {
            title: titleBase,
            description: newEvolutionNotes.slice(0, 300) || undefined,
            due_date: returnReminderDate,
            due_time: returnReminderTime || undefined,
            type: 'return',
            origin: 'appointment',
            patient_id: activeAppt.patient_id || patientId || undefined,
            patient_name: patientName || undefined,
          };

          await axios.post(`${API_URL}/reminders`, payload, {
            headers: { ...headers, 'Content-Type': 'application/json' },
          });
        } catch (err) {
          console.error('Erro ao criar lembrete de retorno:', err);
          toast?.error?.(
            'Evolução salva, mas houve erro ao criar o lembrete de retorno.'
          );
        }
      }

      const updatedActive = { ...activeAppt, ...updateData };
      localStorage.setItem('activeAppointment', JSON.stringify(updatedActive));
      setActiveAppt(updatedActive);
      toast?.success?.('Evolução salva. Agora finalize o atendimento.');
      setShowFinancialCompletion(true);
      window.dispatchEvent(new CustomEvent('appointments:changed'));
    } catch (e) {
      console.error('Erro ao salvar evolução:', e);
      toast?.error?.(
        e?.response?.data?.detail || 'Erro ao salvar evolução.'
      );
    } finally {
      setSavingEvolution(false);
    }
  };

  const treatmentOptions = useMemo(
    () =>
      (treatments || []).map((t) => ({
        value: t.id,
        label: t.procedure || t.name || t.title || 'Tratamento',
      })),
    [treatments]
  );

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Agendamentos ({sorted.length})
        </h3>
        {can('appointments.create') && <button
          onClick={openCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Agendamento
        </button>}
      </div>

      {/* Caixa de Evolução Clínica exibida quando existe atendimento em andamento */}
      {can('clinical_records.update') && activeAppt && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-6">
          <h4 className="text-lg font-bold text-green-800 mb-3 flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Paciente em Atendimento
          </h4>
          <p className="text-sm text-green-700 mb-3">
            Registre a evolução clínica. Ao salvar, confirme o valor e a forma
            de pagamento para concluir o atendimento.
          </p>

          <div className="space-y-3">
            <textarea
              rows={4}
              className="w-full p-3 border border-green-300 rounded-lg focus:ring-green-500 focus:border-green-500"
              placeholder="Descreva o que foi realizado neste atendimento..."
              value={activeEvolutionText}
              onChange={(e) => setActiveEvolutionText(e.target.value)}
            />

            <div className="flex items-center gap-2 flex-wrap">
              <select
                className="p-2 border border-green-300 rounded-lg text-sm"
                value={activeTreatmentId}
                onChange={(e) => setActiveTreatmentId(e.target.value)}
              >
                <option value="">
                  Vincular a um Tratamento (opcional)
                </option>
                {treatmentOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>

              {alignerTreatments.length > 0 && (
                <section className="basis-full rounded-xl border border-purple-200 bg-purple-50 p-4">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-purple-300 text-purple-600"
                      checked={updateAlignerTreatment}
                      onChange={(e) => setUpdateAlignerTreatment(e.target.checked)}
                    />
                    <span>
                      <span className="flex items-center gap-2 text-sm font-semibold text-purple-900">
                        <Sparkles className="h-4 w-4" />
                        Atualizar também o acompanhamento do alinhador
                      </span>
                      <span className="mt-1 block text-xs text-purple-700">
                        A mesma descrição será usada. Você não precisará registrá-la novamente na aba Alinhadores.
                      </span>
                    </span>
                  </label>

                  {updateAlignerTreatment && (
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                      {alignerTreatments.length > 1 && (
                        <label className="block">
                          <span className="mb-1 block text-xs font-medium text-purple-900">
                            Tratamento de alinhadores
                          </span>
                          <select
                            className="w-full rounded-lg border border-purple-200 bg-white p-2 text-sm"
                            value={activeAlignerTreatmentId}
                            onChange={(e) => {
                              const nextId = e.target.value;
                              const next = alignerTreatments.find((item) => item.id === nextId);
                              setActiveAlignerTreatmentId(nextId);
                              setAlignerSituationAfter(getSimpleAlignerSituation(next));
                              setAlignerNextSetReceived(next?.next_set_received === true);
                              setAlignerNextSetReceivedAt(next?.next_set_received_at || '');
                              setAlignerNextSetStart(next?.next_set_start_stage ?? '');
                              setAlignerNextSetEnd(next?.next_set_end_stage ?? '');
                              setAlignerTotalStages(next?.total_stages || '');
                              setAlignerAllDelivered(hasAllAlignersDelivered(next));
                            }}
                          >
                            <option value="">Selecione o tratamento</option>
                            {alignerTreatments.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.provider || 'Alinhadores'} · {item.professional_name || 'Profissional não informado'}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}

                      <label className="block">
                        <span className="mb-1 block text-xs font-medium text-purple-900">
                          Situação após este atendimento
                        </span>
                        <select
                          className="w-full rounded-lg border border-purple-200 bg-white p-2 text-sm"
                          value={alignerSituationAfter}
                          onChange={(e) => setAlignerSituationAfter(e.target.value)}
                        >
                          {Object.entries(ALIGNER_SITUATIONS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </label>

                      {!['completed', 'awaiting_retention'].includes(alignerSituationAfter) && !alignerAllDelivered && <label className="flex items-start gap-3 rounded-lg border border-purple-200 bg-white p-3 md:col-span-2">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4"
                          checked={alignerNextSetReceived}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAlignerNextSetReceived(checked);
                            setAlignerNextSetReceivedAt(checked ? (alignerNextSetReceivedAt || activeAppt?.appointment_date || '') : '');
                          }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-semibold text-purple-900">Os próximos alinhadores chegaram à clínica</span>
                          {alignerNextSetReceived && (
                            <div className="mt-2 grid gap-2 sm:grid-cols-2">
                              <div className="flex items-center gap-2">
                                <input type="number" min="1" required placeholder="Do" className="w-full rounded-lg border border-purple-200 p-2 text-sm" value={alignerNextSetStart} onChange={(e) => setAlignerNextSetStart(e.target.value)} />
                                <span className="text-xs text-purple-800">até</span>
                                <input type="number" min="1" required placeholder="Até" className="w-full rounded-lg border border-purple-200 p-2 text-sm" value={alignerNextSetEnd} onChange={(e) => setAlignerNextSetEnd(e.target.value)} />
                              </div>
                              <input type="number" min="1" placeholder="Total previsto" className="w-full rounded-lg border border-purple-200 p-2 text-sm" value={alignerTotalStages} onChange={(e) => setAlignerTotalStages(e.target.value)} />
                              <input type="date" className="w-full rounded-lg border border-purple-200 p-2 text-sm sm:col-span-2 sm:w-56" value={alignerNextSetReceivedAt} onChange={(e) => setAlignerNextSetReceivedAt(e.target.value)} />
                            </div>
                          )}
                        </span>
                      </label>}

                      {alignerSituationAfter !== 'completed' && <label className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3 md:col-span-2">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4"
                          checked={alignerAllDelivered}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAlignerAllDelivered(checked);
                            if (checked) {
                              setAlignerNextSetReceived(false);
                              setAlignerNextSetReceivedAt('');
                              setAlignerNextSetStart('');
                              setAlignerNextSetEnd('');
                            }
                          }}
                        />
                        <span>
                          <span className="block text-xs font-semibold text-green-900">Todos os alinhadores já foram entregues ao paciente</span>
                          <span className="mt-1 block text-xs text-green-700">Esta informação é independente da situação clínica, como “Em refinamento”.</span>
                        </span>
                      </label>}
                    </div>
                  )}
                </section>
              )}

              <button
                onClick={handleSaveEvolution}
                className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center text-sm"
                disabled={savingEvolution}
              >
                <Save className="w-4 h-4 mr-1" />
                {savingEvolution ? 'Salvando...' : 'Salvar evolução e finalizar'}
              </button>

              <button
                type="button"
                onClick={handleCancelActiveAppointment}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors flex items-center text-sm"
                disabled={savingEvolution}
              >
                Cancelar atendimento
              </button>              
            </div>

            <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm space-y-3">
              <label className="inline-flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-amber-400 text-amber-600"
                  checked={createReturnReminder}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setCreateReturnReminder(checked);
                    if (checked && !returnReminderDate) {
                      setReturnReminderDate(addMonthsToDate(activeAppt?.appointment_date, 1));
                    }
                  }}
                />
                <span>
                  <span className="block text-sm font-semibold text-amber-900">
                    📅 Programar retorno deste paciente - Lembrete
                  </span>
                  <span className="mt-1 block text-xs text-amber-800">
                    Agende agora o próximo retorno para não esquecer o acompanhamento do tratamento.
                  </span>
                </span>
              </label>

              {createReturnReminder && (
                <div className="rounded-lg border border-amber-200 bg-white p-3 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-medium text-amber-900">
                      Retorno rápido:
                    </span>

                    <button
                      type="button"
                      onClick={() => setReturnReminderDate(addMonthsToDate(activeAppt?.appointment_date, 1))}
                      className="rounded-full bg-amber-200 px-3 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-300"
                    >
                      1 mês
                    </button>

                    <button
                      type="button"
                      onClick={() => setReturnReminderDate(addMonthsToDate(activeAppt?.appointment_date, 3))}
                      className="rounded-full bg-amber-200 px-3 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-300"
                    >
                      3 meses
                    </button>

                    <button
                      type="button"
                      onClick={() => setReturnReminderDate(addMonthsToDate(activeAppt?.appointment_date, 6))}
                      className="rounded-full bg-amber-200 px-3 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-300"
                    >
                      6 meses
                    </button>

                    <span className="text-[11px] font-medium text-amber-800">
                      ou escolha uma data personalizada abaixo
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div>
                      <span className="block text-[11px] text-gray-600 mb-1">
                        Data do retorno
                      </span>
                      <input
                        type="date"
                        className="w-full border rounded-lg px-2 py-1.5 text-xs"
                        value={returnReminderDate}
                        onChange={(e) => setReturnReminderDate(e.target.value)}
                      />
                    </div>

                    <div>
                      <span className="block text-[11px] text-gray-600 mb-1">
                        Horário (opcional)
                      </span>
                      <input
                        type="time"
                        className="w-full border rounded-lg px-2 py-1.5 text-xs"
                        value={returnReminderTime}
                        onChange={(e) => setReturnReminderTime(e.target.value)}
                      />
                    </div>

                    <div className="md:col-span-3">
                      <span className="block text-[11px] text-gray-600 mb-1">
                        Título do lembrete (opcional)
                      </span>
                      <input
                        type="text"
                        className="w-full border rounded-lg px-2 py-1.5 text-xs"
                        value={returnReminderTitle}
                        onChange={(e) => setReturnReminderTitle(e.target.value)}
                        placeholder="Retorno deste atendimento"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lista de agendamentos */}
      {appointmentLoadError ? (
        <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p>{appointmentLoadError}</p>
          <button type="button" onClick={() => onDataUpdate?.()} className="mt-3 rounded-md border border-amber-500 px-3 py-2 font-medium hover:bg-amber-100">Tentar novamente</button>
        </div>
      ) : appointmentsLoading ? (
        <div role="status" className="rounded-lg bg-gray-50 p-6 text-center text-sm text-gray-600">Carregando agendamentos…</div>
      ) : sorted.length > 0 ? (
        <div className="space-y-4">
          {sorted.map((apt) => (
            <div key={apt.id} className="bg-gray-50 p-4 rounded-lg border">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: statusColor(apt.status) }}
                  />
                  <h4 className="font-semibold text-gray-900">
                    {apt.procedure || 'Consulta'}
                  </h4>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${statusColor(apt.status)}22`,
                      color: statusColor(apt.status),
                    }}
                  >
                    {apt.status}
                  </span>
                  {can('appointments.update') && <button
                    onClick={() => openEdit(apt)}
                    className="p-1.5 rounded-full hover:bg-gray-100"
                    title="Editar"
                  >
                    <Edit3 className="w-4 h-4 text-gray-600" />
                  </button>}
                  {can('appointments.delete') && <button
                    onClick={() => handleDelete(apt)}
                    className="p-1.5 rounded-full hover:bg-red-50"
                    title="Excluir"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>}
                </div>
              </div>

              <div className="flex items-center text-sm text-gray-600 mb-2">
                <CalendarIcon className="w-4 h-4 mr-2" />
                <span>{apt.appointment_date}</span>
                <Clock className="w-4 h-4 ml-4 mr-2" />
                <span>
                  {apt.start_time || '—'}
                  {apt.end_time ? ` às ${apt.end_time}` : ''}
                </span>
              </div>

              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-700">
                  {apt.evolution_notes
                    ? `Evolução: ${apt.evolution_notes.substring(0, 60)}${
                        apt.evolution_notes.length > 60 ? '...' : ''
                      }`
                    : 'Sem evolução registrada.'}
                </span>
                {can('appointments.update') && can('clinical_records.update') && canStartAppointment(apt) && (
                  <button
                    onClick={() => handleCheckIn(apt)}
                    className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    Iniciar Atendimento
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <Activity className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-600">
            Nenhum agendamento encontrado para este paciente.
          </p>
        </div>
      )}

      {/* Modal de criação/edição */}
      {showForm && (
        <AppointmentFormModal
          open={showForm}
          onClose={handleFormClose}
          initialData={editing ? { ...editing } : initialAppointmentData}
          patients={currentPatientForModal ? [currentPatientForModal] : []}
          treatments={treatments}
          professionals={getActiveProfessionals()}
          currentPatient={currentPatientForModal}
          toast={toast}
          onSaved={() => {
            onDataUpdate && onDataUpdate();
            window.dispatchEvent(new CustomEvent('appointments:changed'));
          }}
        />
      )}

      <AppointmentFinancialCompletionModal
        open={showFinancialCompletion}
        appointment={activeAppt}
        toast={toast}
        onClose={() => setShowFinancialCompletion(false)}
        onCompleted={() => {
          setShowFinancialCompletion(false);
          localStorage.removeItem('activeAppointment');
          setActiveAppt(null);
          setActiveEvolutionText('');
      setActiveTreatmentId('');
      setActiveAlignerTreatmentId('');
      setUpdateAlignerTreatment(false);
      setAlignerSituationAfter('follow_up_only');
          setActiveAlignerTreatmentId('');
          setUpdateAlignerTreatment(false);
          setAlignerSituationAfter('follow_up_only');
          setCreateReturnReminder(false);
          setReturnReminderDate('');
          setReturnReminderTime('');
          setReturnReminderTitle('');
          window.dispatchEvent(new CustomEvent('appointments:changed'));
          onDataUpdate && onDataUpdate();
          onAppointmentCompleted && onAppointmentCompleted();
        }}
      />
    </div>
  );
}
